import chroma from "chroma-js";
import type { GlobalToken } from "antd";

// Color scheme types for reference
export type ColorSchemeType =
  | "complement"
  | "triadic"
  | "splitComplement"
  | "analogous"
  | "tetradic"
  | "square"
  | "accent"
  | "qualitative"
  | "sequential"
  | "diverging";

interface ColorSchemeOptions {
  count?: number;
  saturation?: number;
  lightness?: number;
  rotation?: number;
  spread?: number;
}

// Re-export existing types
export enum RampTheme {
  // Data Visualization (Categorical)
  classic = "classic", // Professional distinct colors (formerly tableau)
  accessible = "accessible", // Colorblind-friendly
  material = "material", // Material Design 3.0

  // Data Visualization (Scientific & Statistical)
  scientific = "scientific", // Perceptually uniform for precise visualization
  statistical = "statistical", // Optimized for statistical charts

  // Data Visualization (Sequential)
  sequential_blue = "sequential_blue", // Professional blue progression
  sequential_multi = "sequential_multi", // Multi-hue sequential

  // Data Visualization (Diverging)
  diverging_rb = "diverging_rb", // Red-Blue for pos/neg
  diverging_bg = "diverging_bg", // Brown-Green for environment

  // Business & Finance
  financial = "financial", // Financial Times inspired
  enterprise = "enterprise", // Corporate style

  // Nature Inspired
  earth = "earth", // Earth tones
  forest = "forest", // Forest greens
  ocean = "ocean", // Ocean blues

  // UI Progressions
  neutral = "neutral", // Grayscale progression
  high_contrast = "high_contrast", // Maximum contrast
  modern = "modern", // Clean, professional progression
}

interface ChromaTheme {
  startColor: string;
  endColor: string;
  midPoints?: string[];
  colorSpace: "oklch" | "lab" | "lch";
  description: string;
}

// Enhanced theme definitions using better color science
export const RAMPS: Record<
  RampTheme,
  {
    name: string;
    description: string;
    light: ChromaTheme;
    dark: ChromaTheme;
  }
> = {
  // Data Visualization (Categorical)
  [RampTheme.classic]: {
    name: "Classic",
    description:
      "Professional data visualization palette optimized for distinction",
    light: {
      startColor: "#4E79A7",
      endColor: "#E15759",
      midPoints: [
        "#F28E2B",
        "#76B7B2",
        "#59A14F",
        "#EDC948",
        "#B07AA1",
        "#FF9DA7",
        "#9C755F",
        "#BAB0AC",
      ],
      colorSpace: "lab",
      description: "Professional data visualization colors",
    },
    dark: {
      startColor: "#4E79A7",
      endColor: "#E15759",
      midPoints: [
        "#F28E2B",
        "#76B7B2",
        "#59A14F",
        "#EDC948",
        "#B07AA1",
        "#FF9DA7",
        "#9C755F",
        "#BAB0AC",
      ],
      colorSpace: "lab",
      description: "Vibrant data visualization colors",
    },
  },

  [RampTheme.accessible]: {
    name: "Accessible",
    description: "Colorblind-friendly visualization palette",
    light: {
      startColor: "#006BA4",
      endColor: "#FF800E",
      midPoints: ["#595959", "#A2C8EC", "#FFBC79", "#ABABAB"],
      colorSpace: "lab",
      description: "Accessible visualization colors",
    },
    dark: {
      startColor: "#004C73",
      endColor: "#B35900",
      midPoints: ["#3D3D3D", "#71A1CB", "#CC8F47", "#787878"],
      colorSpace: "lab",
      description: "Deep accessible colors",
    },
  },

  [RampTheme.material]: {
    name: "Material",
    description: "Google's Material Design 3.0 visualization palette",
    light: {
      startColor: "#6750A4",
      endColor: "#B4A7D6",
      midPoints: ["#7CB342", "#FFA726", "#26C6DA", "#EC407A"],
      colorSpace: "oklch",
      description: "Modern material design",
    },
    dark: {
      startColor: "#381E72",
      endColor: "#CAC4D9",
      midPoints: ["#4B6700", "#7B3400", "#006A6A", "#930048"],
      colorSpace: "oklch",
      description: "Deep material tones",
    },
  },

  // Data Visualization (Scientific & Statistical)
  [RampTheme.scientific]: {
    name: "Scientific",
    description: "Perceptually uniform scientific visualization",
    light: {
      startColor: "#440154",
      endColor: "#FDE725",
      midPoints: ["#414487", "#2A788E", "#22A884", "#7AD151"],
      colorSpace: "lab",
      description: "Scientific visualization colors",
    },
    dark: {
      startColor: "#2A0154",
      endColor: "#C4B001",
      midPoints: ["#2A2C87", "#1B5C6E", "#177F64", "#5CA82F"],
      colorSpace: "lab",
      description: "Deep scientific progression",
    },
  },

  [RampTheme.statistical]: {
    name: "Statistical",
    description: "Optimized for statistical visualizations and charts",
    light: {
      startColor: "#30A2DA",
      endColor: "#FC4F30",
      midPoints: ["#E5AE38", "#6D904F", "#8B8B8B", "#17A2B8"],
      colorSpace: "lab",
      description: "Statistical visualization colors",
    },
    dark: {
      startColor: "#2078A7",
      endColor: "#D32F0F",
      midPoints: ["#C89121", "#4E6F35", "#636363", "#0F7A8A"],
      colorSpace: "lab",
      description: "Deep statistical colors",
    },
  },

  // Data Visualization (Sequential)
  [RampTheme.sequential_blue]: {
    name: "Sequential Blue",
    description: "Professional blue progression for sequential data",
    light: {
      startColor: "#E3F2FD",
      endColor: "#1565C0",
      midPoints: ["#90CAF9", "#42A5F5", "#1E88E5"],
      colorSpace: "lab",
      description: "Sequential blue progression",
    },
    dark: {
      startColor: "#0D47A1",
      endColor: "#90CAF9",
      midPoints: ["#1565C0", "#1976D2", "#1E88E5"],
      colorSpace: "lab",
      description: "Deep sequential blue",
    },
  },

  [RampTheme.sequential_multi]: {
    name: "Multi-hue Sequential",
    description: "Sequential colors with multiple hues",
    light: {
      startColor: "#f7fbff",
      endColor: "#08306b",
      midPoints: ["#c6dbef", "#6baed6", "#2171b5"],
      colorSpace: "lab",
      description: "Multi-hue sequential progression",
    },
    dark: {
      startColor: "#08306b",
      endColor: "#f7fbff",
      midPoints: ["#2171b5", "#6baed6", "#c6dbef"],
      colorSpace: "lab",
      description: "Deep multi-hue sequential",
    },
  },

  // Data Visualization (Diverging)
  [RampTheme.diverging_rb]: {
    name: "Red-Blue Diverging",
    description: "Red-Blue diverging color scheme for positive/negative data",
    light: {
      startColor: "#ef8a62",
      endColor: "#67a9cf",
      midPoints: ["#fddbc7", "#f7f7f7", "#d1e5f0"],
      colorSpace: "lab",
      description: "Red-Blue diverging scheme",
    },
    dark: {
      startColor: "#b2182b",
      endColor: "#2166ac",
      midPoints: ["#ef8a62", "#f7f7f7", "#67a9cf"],
      colorSpace: "lab",
      description: "Deep Red-Blue diverging",
    },
  },

  [RampTheme.diverging_bg]: {
    name: "Brown-Green Diverging",
    description: "Brown-Green diverging color scheme for environmental data",
    light: {
      startColor: "#d8b365",
      endColor: "#5ab4ac",
      midPoints: ["#f6e8c3", "#f5f5f5", "#c7eae5"],
      colorSpace: "lab",
      description: "Brown-Green diverging scheme",
    },
    dark: {
      startColor: "#8c510a",
      endColor: "#01665e",
      midPoints: ["#d8b365", "#f5f5f5", "#5ab4ac"],
      colorSpace: "lab",
      description: "Deep Brown-Green diverging",
    },
  },

  // Business & Finance
  [RampTheme.financial]: {
    name: "Financial",
    description: "Financial Times inspired visualization palette",
    light: {
      startColor: "#FFA8B5",
      endColor: "#0F5499",
      midPoints: ["#FF7FAA", "#4A6FB5", "#CC0033", "#990F3D"],
      colorSpace: "lab",
      description: "Financial news style",
    },
    dark: {
      startColor: "#990F3D",
      endColor: "#0F5499",
      midPoints: ["#CC0033", "#4A6FB5", "#FF7FAA", "#FFA8B5"],
      colorSpace: "lab",
      description: "Deep financial news tones",
    },
  },

  [RampTheme.enterprise]: {
    name: "Enterprise",
    description: "Professional enterprise-grade color system",
    light: {
      startColor: "#0F62FE",
      endColor: "#FF832B",
      midPoints: ["#42BE65", "#FF8389", "#8A3FFC", "#33B1FF"],
      colorSpace: "lab",
      description: "Enterprise-grade visualization",
    },
    dark: {
      startColor: "#0043CE",
      endColor: "#BA4E00",
      midPoints: ["#198038", "#DA1E28", "#6929C4", "#1192E8"],
      colorSpace: "lab",
      description: "High-contrast enterprise colors",
    },
  },

  // Nature Inspired
  [RampTheme.earth]: {
    name: "Earth",
    description: "Natural earth tones and minerals",
    light: {
      startColor: "#EFEBE9",
      endColor: "#4E342E",
      colorSpace: "oklch",
      description: "Earth tone progression",
    },
    dark: {
      startColor: "#3E2723",
      endColor: "#BCAAA4",
      colorSpace: "oklch",
      description: "Rich earth progression",
    },
  },

  [RampTheme.forest]: {
    name: "Forest",
    description: "Rich greens from forest landscapes",
    light: {
      startColor: "#F1F8E9",
      endColor: "#33691E",
      colorSpace: "oklch",
      description: "Forest canopy progression",
    },
    dark: {
      startColor: "#1B5E20",
      endColor: "#AED581",
      colorSpace: "oklch",
      description: "Deep forest progression",
    },
  },

  [RampTheme.ocean]: {
    name: "Ocean",
    description: "Serene blues inspired by ocean depths",
    light: {
      startColor: "#E0F7FA",
      endColor: "#006064",
      colorSpace: "oklch",
      description: "Ocean depth progression",
    },
    dark: {
      startColor: "#006064",
      endColor: "#80DEEA",
      colorSpace: "oklch",
      description: "Deep ocean progression",
    },
  },

  // UI Progressions
  [RampTheme.neutral]: {
    name: "Neutral",
    description: "Professional and balanced progression",
    light: {
      startColor: "#FAFAFA",
      endColor: "#424242",
      colorSpace: "oklch",
      description: "Clean neutral progression",
    },
    dark: {
      startColor: "#212121",
      endColor: "#E0E0E0",
      colorSpace: "oklch",
      description: "Rich neutral progression",
    },
  },

  [RampTheme.high_contrast]: {
    name: "High Contrast",
    description: "Maximum contrast for accessibility",
    light: {
      startColor: "#FFFFFF",
      endColor: "#000000",
      colorSpace: "lab",
      description: "Maximum contrast progression",
    },
    dark: {
      startColor: "#000000",
      endColor: "#FFFFFF",
      colorSpace: "lab",
      description: "Inverted contrast progression",
    },
  },

  [RampTheme.modern]: {
    name: "Modern",
    description: "Clean, professional palette with subtle depth",
    light: {
      startColor: "#E6E8FF",
      endColor: "#2F3AB2",
      midPoints: ["#8E94FF"],
      colorSpace: "oklch",
      description: "Clean and professional progression",
    },
    dark: {
      startColor: "#1A1D66",
      endColor: "#B2B6FF",
      colorSpace: "oklch",
      description: "Deep, rich progression for dark mode",
    },
  },
};

// Color modification utilities
const colorTransforms = {
  soften: (color: string, amount: number = 0.2) =>
    chroma(color).desaturate(amount).hex(),

  saturate: (color: string, amount: number = 0.2) =>
    chroma(color).saturate(amount).hex(),

  shift: (color: string, hueShift: number) => {
    const col = chroma(color);
    const [h, s, l] = col.hsl();
    return chroma.hsl((h + hueShift) % 360, s, l).hex();
  },
};

// Add new color generation methods
const colorGenerators = {
  // Generate a bezier interpolation through multiple colors
  bezier: (colors: string[], total: number): string[] =>
    chroma.bezier(colors).scale().correctLightness().colors(total),

  // Generate complementary colors
  complement: (baseColor: string): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    return [base.hex(), chroma.hsl((h + 180) % 360, s, l).hex()];
  },

  // Generate triadic colors
  triadic: (baseColor: string): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    return [
      base.hex(),
      chroma.hsl((h + 120) % 360, s, l).hex(),
      chroma.hsl((h + 240) % 360, s, l).hex(),
    ];
  },

  // Generate split complementary colors
  splitComplement: (baseColor: string): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    return [
      base.hex(),
      chroma.hsl((h + 150) % 360, s, l).hex(),
      chroma.hsl((h + 210) % 360, s, l).hex(),
    ];
  },

  // Generate analogous colors
  analogous: (baseColor: string, count: number = 3): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    const step = 30;
    return Array.from({ length: count }, (_, i) => {
      const shift = (i - Math.floor(count / 2)) * step;
      return chroma.hsl((360 + h + shift) % 360, s, l).hex();
    });
  },

  // Generate a more sophisticated monochromatic scale
  advancedMonotone: (baseColor: string, total: number): string[] => {
    const base = chroma(baseColor);
    return chroma
      .scale([base.luminance(0.9), base, base.luminance(0.1)])
      .mode("lch")
      .correctLightness()
      .colors(total);
  },

  // New advanced generators
  tetradic: (baseColor: string): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    return [
      base.hex(),
      chroma.hsl((h + 90) % 360, s, l).hex(),
      chroma.hsl((h + 180) % 360, s, l).hex(),
      chroma.hsl((h + 270) % 360, s, l).hex(),
    ];
  },

  qualitative: (count: number = 8): string[] => {
    // Optimized for data visualization
    const palette = [
      "#1f77b4",
      "#ff7f0e",
      "#2ca02c",
      "#d62728",
      "#9467bd",
      "#8c564b",
      "#e377c2",
      "#7f7f7f",
      "#bcbd22",
      "#17becf",
    ];
    return palette.slice(0, count);
  },

  sequential: (baseColor: string, count: number = 5): string[] => {
    const base = chroma(baseColor);
    return chroma
      .scale([
        base.luminance(0.95),
        base.luminance(0.75),
        base,
        base.luminance(0.25),
        base.luminance(0.05),
      ])
      .mode("lab")
      .correctLightness()
      .colors(count);
  },

  diverging: (color1: string, color2: string, count: number = 7): string[] => {
    return chroma
      .scale([color1, "#f7f7f7", color2])
      .mode("lab")
      .correctLightness()
      .colors(count);
  },

  accent: (baseColor: string, count: number = 3): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    return Array.from({ length: count }, (_, i) => {
      const hueShift = (i * 137.5) % 360;
      const satMod = 0.1 * Math.sin((i * Math.PI) / count);
      return chroma
        .hsl((h + hueShift) % 360, Math.min(1, Math.max(0.2, s + satMod)), l)
        .hex();
    });
  },

  materialDesign: (baseColor: string): string[] => {
    const base = chroma(baseColor);
    return [50, 100, 200, 300, 400, 500, 600, 700, 800, 900].map((weight) => {
      const factor = (weight - 500) / 400;
      return base
        .set("lab.l", "*" + (1 + factor * 0.4))
        .saturate(factor * 0.2)
        .hex();
    });
  },
};

// Add color blend modes
const blendModes = {
  multiply: (color1: string, color2: string) =>
    chroma.blend(color1, color2, "multiply").hex(),

  screen: (color1: string, color2: string) =>
    chroma.blend(color1, color2, "screen").hex(),

  overlay: (color1: string, color2: string) =>
    chroma.blend(color1, color2, "overlay").hex(),

  darken: (color1: string, color2: string) =>
    chroma.blend(color1, color2, "darken").hex(),

  lighten: (color1: string, color2: string) =>
    chroma.blend(color1, color2, "lighten").hex(),
};

// Maintain existing exports with new implementation
export function singleColor() {
  return "#4E5BA6";
}

export function monoColors() {
  return generateMonotone("#4E5BA6", 5);
}

export function successColors(token?: GlobalToken) {
  if (token) {
    return [
      token.green4,
      token.green5,
      token.green6,
      token.green7,
      token.green8,
    ];
  }
  return generateMonotone("#24A148", 5); // IBM Forest Green
}

export function errorColors(token?: GlobalToken) {
  if (token) {
    return [token.red4, token.red5, token.red6, token.red7, token.red8];
  }
  return generateMonotone("#DA1E28", 5); // IBM Cardinal Red
}

export function fullSpectrumColors() {
  return [
    "#3E4784",
    "#717BBC",
    "#B3B8DB",
    "#3F621A",
    "#669F2A",
    "#ACDC79",
    "#107569",
    "#15B79E",
    "#5FE9D0",
    "#0E6F90",
    "#06AED4",
    "#67E3F9",
    "#C01048",
    "#F63D68",
    "#FEA3B4",
  ];
}

// Core color generation function
function generateMonotone(baseColor: string, total: number): string[] {
  return chroma
    .scale([
      chroma(baseColor).brighten(2),
      baseColor,
      chroma(baseColor).darken(2),
    ])
    .mode("oklch")
    .colors(total);
}

// Main theme color generator
export function themeColors(
  theme: RampTheme,
  mode: "dark" | "light",
  isMonotone: boolean,
  isMuted: boolean,
  isPastel: boolean,
  hueReverse: boolean,
  hueShift: number,
  total: number,
  hCycles?: number
): string[] {
  const themeConfig = RAMPS[theme] || RAMPS[RampTheme.classic];
  const config = themeConfig[mode];

  let colors: string[];

  if (isMonotone) {
    colors = colorGenerators.advancedMonotone(config.startColor, total);
  } else {
    const colorPoints = config.midPoints
      ? [config.startColor, ...config.midPoints, config.endColor]
      : [config.startColor, config.endColor];

    // Use bezier interpolation for smoother transitions
    colors = colorGenerators.bezier(colorPoints, total);
  }

  // Apply modifications with improved handling
  if (isMuted) {
    colors = colors.map((c) =>
      chroma(c)
        .desaturate(1)
        .luminance(chroma(c).luminance() * 1.05) // Slight lightness compensation
        .hex()
    );
  }

  if (isPastel) {
    colors = colors.map((c) =>
      chroma(c)
        .luminance(0.8) // More consistent pastel effect
        .saturate(0.1)
        .hex()
    );
  }

  if (hueShift !== 0) {
    colors = colors.map((c) => colorTransforms.shift(c, hueShift));
  }

  if (hueReverse) {
    colors.reverse();
  }

  return colors;
}

// Utility functions
export function hslToHex(h: number, s: number, l: number): string {
  return chroma.hsl(h, s / 100, l / 100).hex();
}

export function hexToRgb(hex: string): [number, number, number] {
  if (hex === undefined) return [255, 255, 255];
  const [r, g, b] = chroma(hex).rgb();
  return [r, g, b];
}

export function rgbToHsl(
  r: number,
  g: number,
  b: number
): [number, number, number] {
  const [h, s, l] = chroma.rgb(r, g, b).hsl();
  return [h, s * 100, l * 100];
}

export function getContrastTextColor(backgroundColor: string): string {
  // First check if it's a very dark color using luminance
  const luminance = chroma(backgroundColor).luminance();
  if (luminance < 0.15) {
    // Very dark colors should always get white text
    return "#ffffff";
  }

  // For other colors, use contrast ratio with a higher threshold
  return chroma.contrast(backgroundColor, "white") >= 3.5
    ? "#ffffff"
    : "#000000";
}

// Ensure backward compatibility
export const getLuminance = (r: number, g: number, b: number): number => {
  return chroma.rgb(r, g, b).luminance();
};

// Add new export for advanced color generation
export function generateColorScheme(
  baseColor: string,
  scheme: ColorSchemeType,
  options: ColorSchemeOptions = {}
): string[] {
  const {
    count = 5,
    saturation = 1,
    lightness = 0.5,
    rotation = 0,
    spread = 30,
  } = options;

  switch (scheme) {
    case "complement": {
      return colorGenerators.complement(baseColor);
    }
    case "triadic": {
      return colorGenerators.triadic(baseColor);
    }
    case "splitComplement": {
      return colorGenerators.splitComplement(baseColor);
    }
    case "analogous": {
      return colorGenerators.analogous(baseColor, count);
    }
    case "tetradic": {
      return colorGenerators.tetradic(baseColor);
    }
    case "square": {
      return colorGenerators.tetradic(baseColor); // Same as tetradic but with equal spacing
    }
    case "accent": {
      return colorGenerators.accent(baseColor, count);
    }
    case "qualitative": {
      return colorGenerators.qualitative(count);
    }
    case "sequential": {
      return colorGenerators.sequential(baseColor, count);
    }
    case "diverging": {
      const complement = colorGenerators.complement(baseColor)[1];
      return colorGenerators.diverging(baseColor, complement, count);
    }
    default: {
      return [baseColor];
    }
  }
}

// Add new exports for advanced color operations
export function generateMaterialPalette(baseColor: string): string[] {
  return colorGenerators.materialDesign(baseColor);
}

export function blendColors(
  color1: string,
  color2: string,
  mode: keyof typeof blendModes
): string {
  return blendModes[mode](color1, color2);
}

// Add a new function for advanced color manipulation
export function manipulateColor(
  color: string,
  operations: {
    saturate?: number;
    desaturate?: number;
    lighten?: number;
    darken?: number;
    brighten?: number;
    rotate?: number;
  }
): string {
  let result = chroma(color);

  if (operations.saturate) result = result.saturate(operations.saturate);
  if (operations.desaturate) result = result.desaturate(operations.desaturate);
  if (operations.lighten) result = result.brighten(operations.lighten);
  if (operations.darken) result = result.darken(operations.darken);
  if (operations.brighten) result = result.brighten(operations.brighten);
  if (operations.rotate) {
    const [h, s, l] = result.hsl();
    result = chroma.hsl((h + operations.rotate) % 360, s, l);
  }

  return result.hex();
}

export function generateGradient(
  startColor: string,
  endColor: string,
  steps: number
): string[] {
  return chroma.scale([startColor, endColor]).mode("lch").colors(steps);
}

export function getContrastColor(
  backgroundColor: string,
  token?: GlobalToken
): string {
  if (token) {
    const lightContrast = chroma.contrast(
      backgroundColor,
      token.colorTextLightSolid
    );
    const darkContrast = chroma.contrast(backgroundColor, token.colorText);
    return lightContrast > darkContrast
      ? token.colorTextLightSolid
      : token.colorText;
  }
  const contrast = chroma.contrast(backgroundColor, "#ffffff");
  return contrast > 4.5 ? "#ffffff" : "#000000";
}
